import {Button, ControlledInput, LabelRegular, LabelSmallRegular} from '@hm/fundamental'
import {mdiDeleteOutline, mdiPencilOutline, mdiPlus} from '@mdi/js'
import React, {useMemo, useState} from 'react'

import {useTranslation} from '../../hooks/useTranslation'
import {WEBHOOK_ENABLED_API_PRODUCTS} from '../../modules/DataScopeConfiguration'
import {
  CHANGE_WEBHOOK_SUBSCRIPTIONS,
  GroupedRoleReducerAction
} from '../../modules/GroupedRoleAssignmentReducer'
import {GroupedRoleAssignment} from '../../modules/ManageUsers.selectors'

type WebhookSubscriptionsProps = {
  groupedRoleAssignment: GroupedRoleAssignment
  isEditing: boolean
  dispatchRoleStateUpdate: React.Dispatch<GroupedRoleReducerAction>
}

type EditingField = {subscriptionIndex: number; key: string}

const parseWebhookSubscriptions = (
  raw: string | string[] | undefined
): Record<string, string>[] => {
  if (!raw) return []
  const items = Array.isArray(raw) ? raw : [raw]
  return items.reduce<Record<string, string>[]>((acc, jsonString) => {
    if (!jsonString) return acc
    try {
      const parsed = JSON.parse(jsonString)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        acc.push(parsed as Record<string, string>)
      }
    } catch {
      // skip malformed entries
    }
    return acc
  }, [])
}

export const WebhookSubscriptions = ({
  groupedRoleAssignment,
  isEditing,
  dispatchRoleStateUpdate
}: WebhookSubscriptionsProps) => {
  const {t} = useTranslation()

  // add form state
  const [addingToIndex, setAddingToIndex] = useState<number | null>(null)
  const [addingNew, setAddingNew] = useState(false)
  const [newKey, setNewKey] = useState('')
  const [newValue, setNewValue] = useState('')
  const [newKeyError, setNewKeyError] = useState<string | undefined>(undefined)
  const [newValueError, setNewValueError] = useState<string | undefined>(undefined)

  // edit form state
  const [editingField, setEditingField] = useState<EditingField | null>(null)
  const [editKey, setEditKey] = useState('')
  const [editValue, setEditValue] = useState('')
  const [editKeyError, setEditKeyError] = useState<string | undefined>(undefined)
  const [editValueError, setEditValueError] = useState<string | undefined>(undefined)

  const rawScope = groupedRoleAssignment.commonDataScopes.webhookSubscriptions
  const parsed = useMemo(() => parseWebhookSubscriptions(rawScope), [rawScope])

  if (parsed.length === 0 && !isEditing) {
    return null
  }

  const isAddFormOpen = addingToIndex !== null || addingNew
  const isAnyOperationOpen = isAddFormOpen || editingField !== null

  const dispatchUpdate = (updated: Record<string, string>[]) => {
    dispatchRoleStateUpdate({
      type: CHANGE_WEBHOOK_SUBSCRIPTIONS,
      payload: updated
        .filter((m) => Object.keys(m).length > 0)
        .map((m) => JSON.stringify(m))
    })
  }

  // --- add form handlers ---

  const resetAddForm = () => {
    setNewKey('')
    setNewValue('')
    setNewKeyError(undefined)
    setNewValueError(undefined)
    setAddingToIndex(null)
    setAddingNew(false)
  }

  const openAddField = (index: number) => {
    cancelEdit()
    resetAddForm()
    setAddingToIndex(index)
  }

  const openAddNew = () => {
    cancelEdit()
    resetAddForm()
    setAddingNew(true)
  }

  const handleAdd = () => {
    const trimmedKey = newKey.trim()
    const trimmedValue = newValue.trim()
    const targetMap = addingToIndex !== null ? parsed[addingToIndex] : {}

    if (!trimmedKey) {
      setNewKeyError(t('roleAssignment.webhookSubscriptions.keyRequired'))
      return
    }
    if (Object.keys(targetMap).some((k) => k.toLowerCase() === trimmedKey.toLowerCase())) {
      setNewKeyError(t('roleAssignment.webhookSubscriptions.keyExists'))
      return
    }
    if (!trimmedValue) {
      setNewValueError(t('roleAssignment.webhookSubscriptions.valueRequired'))
      return
    }

    setNewKey('')
    setNewValue('')
    setNewKeyError(undefined)
    setNewValueError(undefined)

    if (addingToIndex !== null) {
      dispatchUpdate(
        parsed.map((sub, i) =>
          i === addingToIndex ? {...sub, [trimmedKey]: trimmedValue} : sub
        )
      )
      // close the form once the subscription reaches the 6-field limit
      if (Object.keys(targetMap).length + 1 >= 6) {
        resetAddForm()
      }
    } else {
      // new subscription: create it and pivot the form inside the new card
      dispatchUpdate([...parsed, {[trimmedKey]: trimmedValue}])
      setAddingNew(false)
      setAddingToIndex(parsed.length)
    }
  }

  // --- delete handlers ---

  const handleDeleteField = (subscriptionIndex: number, key: string) => {
    if (editingField?.subscriptionIndex === subscriptionIndex && editingField?.key === key) {
      cancelEdit()
    }
    const updated = parsed.map((sub, i) => {
      if (i !== subscriptionIndex) return sub
      const updatedSub = Object.assign(Object.create(null), sub)
      delete updatedSub[key]
      return updatedSub
    })
    dispatchUpdate(updated)
  }

  const handleDeleteSubscription = (index: number) => {
    if (addingToIndex === index) resetAddForm()
    if (editingField?.subscriptionIndex === index) cancelEdit()
    dispatchUpdate(parsed.filter((_, i) => i !== index))
  }

  // --- edit handlers ---

  const openEdit = (subscriptionIndex: number, key: string, value: string) => {
    resetAddForm()
    setEditingField({subscriptionIndex, key})
    setEditKey(key)
    setEditValue(value)
    setEditKeyError(undefined)
    setEditValueError(undefined)
  }

  const cancelEdit = () => {
    setEditingField(null)
    setEditKey('')
    setEditValue('')
    setEditKeyError(undefined)
    setEditValueError(undefined)
  }

  const handleSaveEdit = () => {
    if (!editingField) return
    const {subscriptionIndex, key: originalKey} = editingField
    const trimmedKey = editKey.trim()
    const trimmedValue = editValue.trim()
    const targetMap = parsed[subscriptionIndex]

    if (!trimmedKey) {
      setEditKeyError(t('roleAssignment.webhookSubscriptions.keyRequired'))
      return
    }
    if (
      trimmedKey.toLowerCase() !== originalKey.toLowerCase() &&
      Object.keys(targetMap).some((k) => k.toLowerCase() === trimmedKey.toLowerCase())
    ) {
      setEditKeyError(t('roleAssignment.webhookSubscriptions.keyExists'))
      return
    }
    if (!trimmedValue) {
      setEditValueError(t('roleAssignment.webhookSubscriptions.valueRequired'))
      return
    }

    dispatchUpdate(
      parsed.map((sub, i) => {
        if (i !== subscriptionIndex) return sub
        const updatedSub = {...sub}
        if (trimmedKey !== originalKey) {
          delete updatedSub[originalKey]
        }
        updatedSub[trimmedKey] = trimmedValue
        return updatedSub
      })
    )
    cancelEdit()
  }

  // --- render helpers ---

  const renderAddForm = () => (
    <div className="flex flex-col gap-3 mt-1" data-cy="webhook-add-form">
      <div className="flex gap-3">
        <div className="flex-1" data-cy="webhook-new-key-input">
          <ControlledInput
            label={t('roleAssignment.webhookSubscriptions.keyLabel')}
            value={newKey}
            onChange={(e) => {
              setNewKey(e.target.value)
              setNewKeyError(undefined)
            }}
            error={newKeyError}
            showValidationErrorOnTouched={false}
          />
        </div>
        <div className="flex-1" data-cy="webhook-new-value-input">
          <ControlledInput
            label={t('roleAssignment.webhookSubscriptions.valueLabel')}
            value={newValue}
            onChange={(e) => {
              setNewValue(e.target.value)
              setNewValueError(undefined)
            }}
            error={newValueError}
            showValidationErrorOnTouched={false}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="primary" type="button" icon={mdiPlus} onClick={handleAdd} data-cy="webhook-add-confirm">
          {t('roleAssignment.webhookSubscriptions.add')}
        </Button>
        <Button variant="secondary" type="button" onClick={resetAddForm} data-cy="webhook-add-cancel">
          {t('roleAssignment.cancel')}
        </Button>
      </div>
    </div>
  )

  const renderEditForm = () => (
    <div className="flex flex-col gap-3 mt-1" data-cy="webhook-edit-form">
      <div className="flex gap-3">
        <div className="flex-1" data-cy="webhook-edit-key-input">
          <ControlledInput
            label={t('roleAssignment.webhookSubscriptions.keyLabel')}
            value={editKey}
            onChange={(e) => {
              setEditKey(e.target.value)
              setEditKeyError(undefined)
            }}
            error={editKeyError}
            showValidationErrorOnTouched={false}
          />
        </div>
        <div className="flex-1" data-cy="webhook-edit-value-input">
          <ControlledInput
            label={t('roleAssignment.webhookSubscriptions.valueLabel')}
            value={editValue}
            onChange={(e) => {
              setEditValue(e.target.value)
              setEditValueError(undefined)
            }}
            error={editValueError}
            showValidationErrorOnTouched={false}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="primary" type="button" onClick={handleSaveEdit} data-cy="webhook-edit-confirm">
          {t('roleAssignment.webhookSubscriptions.confirm')}
        </Button>
        <Button variant="secondary" type="button" onClick={cancelEdit} data-cy="webhook-edit-cancel">
          {t('roleAssignment.cancel')}
        </Button>
      </div>
    </div>
  )

  return (
    <div className="flex flex-col gap-2 max-w-[600px]" data-cy="webhook-subscriptions">
      <LabelRegular className="text-foreground_faded">
        {t('roleAssignment.dataScope.webhookSubscriptions')}
      </LabelRegular>
      <LabelSmallRegular className="text-foreground_faded italic">
        {t('roleAssignment.webhookSubscriptions.subtitle', {products: WEBHOOK_ENABLED_API_PRODUCTS.join(', ')})}
      </LabelSmallRegular>

      {parsed.map((subscription, subscriptionIndex) => (
        <div
          key={subscriptionIndex}
          className="flex flex-col gap-2 border-1px border-border_highlight_10 rounded-lg p-3"
          data-cy={`webhook-subscription-${subscriptionIndex}`}
        >
          {Object.entries(subscription).map(([key, value]) => {
            const isEditingThisField =
              editingField?.subscriptionIndex === subscriptionIndex && editingField?.key === key

            if (isEditingThisField) {
              return <div key={key}>{renderEditForm()}</div>
            }

            return (
              <div
                key={key}
                className="flex gap-2 items-center"
                data-cy={`webhook-field-${subscriptionIndex}-${key}`}
              >
                <div className="flex-1 flex gap-4 items-center min-w-0">
                  <span className="font-medium shrink-0">{key}</span>
                  <span className="text-foreground_faded shrink-0">→</span>
                  <span className="truncate">{value}</span>
                </div>
                {isEditing && !isAnyOperationOpen && (
                  <>
                    <Button
                      variant="secondary"
                      type="button"
                      icon={mdiPencilOutline}
                      onClick={() => openEdit(subscriptionIndex, key, value)}
                      data-cy={`webhook-field-edit-${subscriptionIndex}-${key}`}
                    />
                    <Button
                      variant="secondary"
                      type="button"
                      icon={mdiDeleteOutline}
                      onClick={() => handleDeleteField(subscriptionIndex, key)}
                      data-cy={`webhook-field-delete-${subscriptionIndex}-${key}`}
                    />
                  </>
                )}
              </div>
            )
          })}

          {isEditing && addingToIndex === subscriptionIndex && renderAddForm()}

          {isEditing && !isAnyOperationOpen && (
            <div className="flex gap-2 mt-1">
              {Object.keys(subscription).length < 6 && (
                <Button
                  variant="secondary"
                  type="button"
                  icon={mdiPlus}
                  onClick={() => openAddField(subscriptionIndex)}
                  data-cy={`webhook-add-field-${subscriptionIndex}`}
                >
                  {t('roleAssignment.webhookSubscriptions.addField')}
                </Button>
              )}
              <Button
                variant="secondary"
                type="button"
                icon={mdiDeleteOutline}
                onClick={() => handleDeleteSubscription(subscriptionIndex)}
                data-cy={`webhook-delete-subscription-${subscriptionIndex}`}
              >
                {t('roleAssignment.webhookSubscriptions.removeSubscription')}
              </Button>
            </div>
          )}
        </div>
      ))}

      {isEditing && addingNew && renderAddForm()}

      {isEditing && !isAnyOperationOpen && (
        <div>
          <Button
            variant="secondary"
            type="button"
            icon={mdiPlus}
            onClick={openAddNew}
            data-cy="webhook-add-new"
          >
            {t('roleAssignment.webhookSubscriptions.addNew')}
          </Button>
        </div>
      )}
    </div>
  )
}
